/**
 * `RemoteRosterController` — the UI's roster client.
 *
 * The UI roster is **server-backed only**. The server roster room
 * (`backend/src/rosterRoom.ts`) is authoritative, and this client applies
 * **optimistically** then **reconciles** to the server's broadcast (last-write-wins).
 *
 * It is a **thin subclass of {@link RemoteController}** — the optimistic-apply +
 * LWW-reconcile machinery (dispatch / undo / redo / onMessage / handle / publish /
 * subscribe) lives in the generic, shared with the encounter controller. The only
 * roster-specific bits are kept here:
 *   - the roster wire-message vocabulary ({@link ROSTER_PROTOCOL});
 *   - the local stat **compute mixin** (`computePilot`/`computeAll`) — a
 *     roster-only concern: computed stats never cross the wire, so every client
 *     recomputes from raw state + the local compendium.
 *
 * The roster room is the single global room, joined implicitly on socket-open, so
 * there is no join handshake to override (the base `sendJoin` is a no-op here).
 *
 * Persistence: none here. The roster is the server's state; the client is a
 * live view.
 *
 * Transport is injected as a minimal {@link RemoteConnection} so this is unit-
 * testable against a fake and wired to a real `WebSocket` in `useRemoteRoster`.
 */
import {
  RosterHistory,
  emptyRoster,
  type RosterCommand,
  type RosterState,
} from "@lancer-vtt/domain";
import type { Compendium } from "@lancer-vtt/compendium";
import { compute, type ComputedPilot } from "@lancer-vtt/rules";
import {
  RemoteController,
  type RemoteConnection,
  type RemoteProtocol,
  type RemoteSnapshot,
  type DispatchResult,
} from "../remoteController";

/** Re-exported for callers/tests that wired the controller's transport surface. */
export type { RemoteConnection as RosterConnection };
export type { RemoteSnapshot, DispatchResult };

/** The roster room's wire-message vocabulary (the generic switches on these). */
const ROSTER_PROTOCOL: RemoteProtocol = {
  command: "roster_command",
  undo: "roster_undo",
  redo: "roster_redo",
  welcome: "roster_welcome",
  change: "roster_change",
  rejected: "roster_command_rejected",
};

export class RemoteRosterController extends RemoteController<RosterState, RosterCommand> {
  private readonly compendium: Compendium;

  constructor(compendium: Compendium, conn: RemoteConnection, initial: RosterState = emptyRoster()) {
    super({
      conn,
      protocol: ROSTER_PROTOCOL,
      initial: new RosterHistory(initial, compendium),
      reseed: (state) => new RosterHistory(state, compendium),
    });
    this.compendium = compendium;
  }

  protected notConnectedError(): string {
    return "roster room not connected";
  }

  // ── Roster-only compute mixin (computed stats never cross the wire) ───────────

  computePilot(pilotId: string): ComputedPilot | undefined {
    const pilot = this.getSnapshot().state.pilots[pilotId];
    if (!pilot) return undefined;
    return compute(pilot, this.compendium).pilot;
  }

  computeAll(): Record<string, ComputedPilot> {
    const out: Record<string, ComputedPilot> = {};
    for (const pilot of Object.values(this.getSnapshot().state.pilots)) {
      out[pilot.id] = compute(pilot, this.compendium).pilot;
    }
    return out;
  }
}
